// M11, suite du lot L5-A : présences, contrats, vues du chef, formations, évaluations, synthèse agrégée.
// Aucune donnée de paie. Les vues du chef et la synthèse ne portent ni motif ni contenu d'évaluation.
import { z } from 'zod';
import { ETATS_PRESENCE, STATUTS_AGENT } from './personnel';
import { TYPES_CONGE } from './personnel-conges';

const id = z.string().uuid();
const jour = z.iso.date();

export const ETATS_CONGE = ['demande', 'valide_chef', 'valide_rh', 'refuse', 'annule'] as const;
export type EtatCongePersonnel = (typeof ETATS_CONGE)[number];
export const LIBELLES_ETAT_CONGE: Record<EtatCongePersonnel, string> = {
  demande: 'À valider par le chef',
  valide_chef: 'À valider par les RH',
  valide_rh: 'Validé',
  refuse: 'Refusé',
  annule: 'Annulé',
};
export const LIBELLES_TYPE_CONGE: Record<(typeof TYPES_CONGE)[number], string> = {
  annuel: 'Congé annuel',
  maladie: 'Congé maladie',
  maternite: 'Congé de maternité',
  exceptionnel: 'Autorisation exceptionnelle',
};
export const LIBELLES_STATUT_AGENT: Record<(typeof STATUTS_AGENT)[number], string> = {
  fonctionnaire: 'Fonctionnaire',
  contractuel: 'Contractuel',
  journalier: 'Journalier',
  stagiaire: 'Stagiaire',
};
export const LIBELLES_ETAT_PRESENCE: Record<(typeof ETATS_PRESENCE)[number], string> = {
  present: 'Présent',
  absent: 'Absent',
  mission: 'En mission',
  conge: 'En congé',
};

/** Saisie quotidienne : une ligne par agent pour le jour donné (RH, ou chef pour son service). */
export const schemaSaisiePresences = z.strictObject({
  jour,
  lignes: z
    .array(z.strictObject({ agent_id: id, etat: z.enum(ETATS_PRESENCE) }))
    .min(1)
    .max(500)
    .refine((l) => new Set(l.map((x) => x.agent_id)).size === l.length, 'Agent répété'),
});

/** Import CSV (colonnes matricule, jour, etat) : tout ou rien, rapport par numéro de ligne. */
export const schemaImportPresencesCsv = z.strictObject({
  contenu: z.string().min(1).max(2_000_000),
  cle_idempotence: id,
});

export const schemaFiltreCongesPersonnel = z.strictObject({
  etat: z.enum(ETATS_CONGE).optional(),
  service_id: id.optional(),
  agent_id: id.optional(),
  annee: z.coerce.number().int().min(2000).max(2200).optional(),
});

export const schemaAnnulationConge = z.strictObject({
  motif: z.string().trim().min(3).max(1000),
});

export const schemaModificationParticipation = z.strictObject({
  etat: z.enum(['inscrit', 'present', 'absent', 'termine']),
});

export const schemaFiltreEcheancesContrats = z.strictObject({
  jours: z.coerce.number().int().min(1).max(365).default(60),
});

/** Ligne d'une liste de congés vue par le chef : nécessaire à la décision, sans motif. */
export interface CongeVueValidation {
  id: string;
  agent_id: string;
  matricule: string;
  nom: string;
  prenoms: string;
  service_id: string;
  service_nom: string;
  type: (typeof TYPES_CONGE)[number];
  debut: string;
  fin: string;
  debut_demi_journee: number;
  fin_demi_journee: number;
  demi_journees: number;
  etat: EtatCongePersonnel;
  cree_le: string;
}

export interface PresenceVue {
  agent_id: string;
  matricule: string;
  nom: string;
  prenoms: string;
  service_nom: string | null;
  jour: string;
  etat: (typeof ETATS_PRESENCE)[number] | null;
}

export interface RapportImportPresences {
  id: string | null;
  lignes: number;
  acceptees: number;
  identiques: number;
  erreurs: { ligne: number; code: string }[];
  deja_importe: boolean;
}

/** Synthèse du maire et du SG : nombres seulement, aucun nom ni motif. */
export interface SynthesePersonnel {
  date_reference: string;
  agents: number;
  par_statut: { statut: string; nombre: number }[];
  par_service: { service: string; nombre: number }[];
  presences_du_jour: { etat: string; nombre: number }[];
  conges_en_cours: number;
  demandes_en_attente: { etat: string; nombre: number }[];
  contrats_echeance_60_jours: number;
  formations_a_venir: number;
}
